<template>
  <div class="custo-update-report-modal">
    <h3 class="title">{{ $t('custo_update_report_titulo') }}</h3>
    <p class="generated-at" v-if="report.geradoEm">{{ $t('custo_update_report_gerado_em') }}: {{ formattedGeradoEm }}</p>

    <div class="summary-cards">
      <div class="summary-card">
        <span class="value">{{ report.totalPratos || 0 }}</span>
        <span class="label">{{ $t('custo_update_report_total_pratos') }}</span>
      </div>
      <div class="summary-card">
        <span class="value">{{ report.totalComProblema || 0 }}</span>
        <span class="label">{{ $t('custo_update_report_total_problema') }}</span>
      </div>
      <div class="summary-card">
        <span class="value">{{ report.totalProdutos || 0 }}</span>
        <span class="label">{{ $t('custo_update_report_total_produtos') }}</span>
      </div>
      <div
        v-for="tipo in tiposProblemaComOcorrencia"
        :key="tipo.key"
        class="summary-card problem-type"
      >
        <span class="value">{{ tipo.count }}</span>
        <span class="label">{{ tipo.label }}</span>
      </div>
    </div>

    <div class="pratos-table-container">
      <table v-if="pratos.length" class="pratos-table">
        <thead>
          <tr>
            <th>{{ $t('custo_update_report_prato') }}</th>
            <th>{{ $t('custo_update_report_custo_anterior') }}</th>
            <th>{{ $t('custo_update_report_custo_novo') }}</th>
            <th>{{ $t('custo_update_report_problemas') }}</th>
            <th>{{ $t('custo_update_report_produtos_titulo') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="prato in pratos" :key="prato.id_prato">
            <td class="plate-cell">
              <span class="plate-id">{{ prato.id_prato }}</span>
              <span class="plate-name">{{ prato.nm_prato }}</span>
            </td>
            <td>{{ formatCurrency(prato.custoAnterior) }}</td>
            <td>{{ formatCurrency(prato.custoNovo) }}</td>
            <td>
              <span
                v-for="problema in (prato.problemas || [])"
                :key="problema"
                class="badge"
              >{{ problemaLabel(problema) }}</span>
            </td>
            <td>
              <span
                class="products-summary"
                :title="produtosTooltip(prato.produtos)"
              >{{ produtosResumo(prato.produtos) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="empty-state">{{ $t('custo_update_report_sem_problemas') }}</div>
    </div>

    <div class="buttons-container">
      <div class="button ghost" id="export" @click="exportXls">
        <i class="fas fa-file-export"></i> {{ $t('custo_update_report_export_xls') }}
      </div>
      <div class="button" id="close" @click="close">{{ $t('Fechar') }}</div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { t } from 'i18next';
import ExcelBuilder from "@/application/libs/excelBuilder";

const PROBLEMA_LABEL_KEYS = {
  CUSTO_ZERADO: 'problema_tipo_custo_zerado',
  CUSTO_DESATUALIZADO: 'problema_tipo_custo_desatualizado',
  PRATO_INATIVO: 'problema_tipo_prato_inativo',
  RECEITA_INATIVA: 'problema_tipo_receita_inativa',
  PRODUTO_INATIVO_FILIAL: 'problema_tipo_produto_inativo_filial',
  PRODUTO_INATIVO: "problema_produto_inativo"
};

export default {
  name: "CustoUpdateReportModal",
  props: {
    report: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    pratos() {
      return this.report?.pratos || [];
    },
    formattedGeradoEm() {
      return this.report?.geradoEm
        ? moment(this.report.geradoEm).format('DD/MM/YYYY HH:mm:ss')
        : '';
    },
    tiposProblemaComOcorrencia() {
      const porTipoProblema = this.report?.porTipoProblema || {};
      return Object.keys(porTipoProblema)
        .filter(key => porTipoProblema[key] > 0)
        .map(key => ({
          key,
          count: porTipoProblema[key],
          label: this.problemaLabel(key)
        }));
    }
  },
  methods: {
    problemaLabel(problema) {
      const key = PROBLEMA_LABEL_KEYS[problema];
      return key ? t(key) : problema;
    },
    formatCurrency(value) {
      const parsed = parseFloat(value || 0);
      const [intPart, decPart] = parsed.toFixed(5).split('.');
      const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return `R$ ${intFormatted},${decPart}`;
    },
    produtosResumo(produtos) {
      const lista = produtos || [];
      if (!lista.length) return '--';
      const primeiros = lista.slice(0, 2)
        .map(p => `${p.cod_arv_produto} - ${p.nome_produto}`)
        .join('; ');
      const restante = lista.length - 2;
      return `${lista.length} produto(s): ${primeiros}${restante > 0 ? ` +${restante} mais` : ''}`;
    },
    produtosTooltip(produtos) {
      const lista = produtos || [];
      return lista
        .map(p => `${p.cod_arv_produto} - ${p.nome_produto} (${(p.problemas || []).map(pr => this.problemaLabel(pr)).join(', ')})`)
        .join('\n');
    },
    exportXls() {
      const geradoEm = this.formattedGeradoEm;
      const porTipoProblema = this.report?.porTipoProblema || {};

      const resumoData = [
        { campo: t('custo_update_report_gerado_em'), valor: geradoEm },
        { campo: t('custo_update_report_total_pratos'), valor: this.report?.totalPratos || 0 },
        { campo: t('custo_update_report_total_problema'), valor: this.report?.totalComProblema || 0 },
        { campo: t('custo_update_report_total_produtos'), valor: this.report?.totalProdutos || 0 },
        ...Object.keys(porTipoProblema).map(key => ({
          campo: this.problemaLabel(key),
          valor: porTipoProblema[key]
        }))
      ];
      const resumoLabel = [
        { header: t('custo_update_report_problemas'), key: 'campo' },
        { header: '#', key: 'valor' }
      ];

      const pratosData = this.pratos.map(prato => ({
        id_prato: prato.id_prato,
        nm_prato: prato.nm_prato,
        custoAnterior: prato.custoAnterior,
        custoNovo: prato.custoNovo,
        problemas: (prato.problemas || []).map(p => this.problemaLabel(p)).join(', '),
        qtdProdutosComProblema: (prato.produtos || []).length
      }));
      const pratosLabel = [
        { header: t('custo_update_report_prato'), key: 'id_prato' },
        { header: t('custo_update_report_nome_prato'), key: 'nm_prato' },
        { header: t('custo_update_report_custo_anterior'), key: 'custoAnterior' },
        { header: t('custo_update_report_custo_novo'), key: 'custoNovo' },
        { header: t('custo_update_report_problemas'), key: 'problemas' },
        { header: t('custo_update_report_produtos_titulo'), key: 'qtdProdutosComProblema' }
      ];

      const produtosData = [];
      this.pratos.forEach(prato => {
        (prato.produtos || []).forEach(produto => {
          const problemasDoProduto = produto.problemas && produto.problemas.length
            ? produto.problemas
            : [null];
          problemasDoProduto.forEach(problema => {
            produtosData.push({
              id_prato: prato.id_prato,
              nm_prato: prato.nm_prato,
              cod_produto: produto.cod_arv_produto,
              nome_produto: produto.nome_produto,
              vlr_produto: produto.vlr_produto,
              problema: problema ? this.problemaLabel(problema) : ''
            });
          });
        });
      });
      const produtosLabel = [
        { header: t('custo_update_report_prato'), key: 'id_prato' },
        { header: t('custo_update_report_nome_prato'), key: 'nm_prato' },
        { header: t('custo_update_report_cod_produto'), key: 'cod_produto' },
        { header: t('custo_update_report_nome_produto'), key: 'nome_produto' },
        { header: t('custo_update_report_valor_produto'), key: 'vlr_produto' },
        { header: t('custo_update_report_problemas'), key: 'problema' }
      ];

      ExcelBuilder.fromJsonMultipleTabs([
        { tabName: 'Resumo', jsonData: resumoData, colDetails: resumoLabel },
        { tabName: 'Pratos', jsonData: pratosData, colDetails: pratosLabel },
        { tabName: 'Produtos', jsonData: produtosData, colDetails: produtosLabel }
      ], t('custo_update_report_titulo'));
    },
    close() {
      this.$emit('close');
    }
  }
};
</script>

<style scoped lang="less">
.custo-update-report-modal {
  width: calc(100% - 30px);
  margin: 25px;

  .title {
    margin: 0 0 5px;
    font-size: 1.2rem;
    font-weight: 600;
  }

  .generated-at {
    margin: 0 0 15px;
    color: #555;
    font-size: 0.85rem;
  }

  .summary-cards {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 15px;
  }

  .summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10px 16px;
    border-radius: 6px;
    background: #f7f7f7;
    border: 1px solid #e0e0e0;
    min-width: 100px;

    .value {
      font-size: 1.4rem;
      font-weight: 700;
      color: #212146;
    }

    .label {
      font-size: 0.75rem;
      color: #666;
      text-align: center;
    }

    &.problem-type {
      background: #FFF5F5;
      border-color: #FFCDD2;

      .value {
        color: #dc143d;
      }
    }
  }

  .pratos-table-container {
    max-height: 50dvh;
    overflow-y: auto;
    border-radius: 6px;
    border: 1px solid #e7e7e7;
  }

  .pratos-table {
    width: 100%;
    border-collapse: collapse;

    th,
    td {
      padding: 8px 10px;
      border-bottom: 1px solid #e7e7e7;
      text-align: left;
      vertical-align: top;
      font-size: 0.85rem;
    }

    th {
      background: #212146;
      color: white;
      font-weight: 600;
      position: sticky;
      top: 0;
      z-index: 1;
    }

    tbody tr:nth-child(odd) {
      background-color: #f7f7f7;
    }

    tbody tr:nth-child(even) {
      background-color: #ffffff;
    }

    tbody tr:hover {
      background-color: #e8f4f8;
    }
  }

  .plate-cell {
    display: flex;
    flex-direction: column;

    .plate-id {
      font-weight: 600;
      color: #212146;
    }

    .plate-name {
      color: #555;
    }
  }

  .badge {
    display: inline-block;
    padding: 2px 8px;
    margin: 0 4px 4px 0;
    border-radius: 10px;
    background: #FFEBEE;
    border: 1px solid #FFCDD2;
    color: #c62828;
    font-size: 0.72rem;
    white-space: nowrap;
  }

  .products-summary {
    cursor: help;
    white-space: nowrap;
  }

  .empty-state {
    padding: 12px;
    background: #f7f7f7;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    text-align: center;
  }

  .buttons-container {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin: 15px 0 5px;

    .button {
      cursor: pointer;
      padding: 8px 16px;
      border-radius: 4px;
      text-align: center;
      background: #dc143d;
      color: white;
      font-size: 0.9rem;

      &:hover {
        background: #b01030;
      }

      &.ghost {
        background: white;
        color: #dc143d;
        border: 1px solid #dc143d;

        &:hover {
          color: #212146;
          border-color: #212146;
        }
      }
    }
  }
}
</style>
